import React, { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Breadcrumb, Layout, PageHeader } from 'antd'
import { useLocation, useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
	CheckOutlined,
	CloseCircleOutlined,
	DeleteOutlined,
	EyeOutlined,
	UserSwitchOutlined,
} from '@ant-design/icons'

// Hooks
import useDebounce from 'hooks/useDebounce'
import useSearchParams from 'hooks/useSearchParams'
import useGetColumns from './hooks/useGetColumns'
import useFilters from 'components/filterButton/useFilters'

// Services
import {
	getDemands,
	setResponsible,
	updateStatus,
	getCategoriesOptions,
	getTemplatesOptions,
	getUsersOptions,
	getSolicitantAreasOptions,
	deleteTask,
} from 'services/demands/demandsServices'
import { errorMessage, successMessage } from 'services/messager'

// Components
import MainLayout from 'components/mainLayout'
import DataTable from 'components/dataTable'
import ConfirmationModal from '../../components/ConfirmationModal'
import SetResponsibleModal from './components/SetResponsibleModal'
import { FilterSidebar, FilterButton } from 'components/filterButton'

// Utils
import { processStatusFilters } from 'utils/filters'

const statusOptions = [
	{ label: 'Aguardando Ação', value: 'Aguardando ação' },
	{ label: 'Em andamento', value: 'GROUP_IN_PROGRESS' },
	{ label: 'Concluído', value: 'Concluído' },
	{ label: 'Cancelado', value: 'Cancelado' },
]

const priorityOptions = [
	{ label: 'Urgente', value: 'Urgente' },
	{ label: 'Alta', value: 'Alta' },
	{ label: 'Média', value: 'Média' },
	{ label: 'Baixa', value: 'Baixa' },
]

const Demands = () => {
	const location = useLocation()
	const history = useHistory()
	const searchParams = useSearchParams()
	const queryClient = useQueryClient()
	const user = useSelector(({ session }) => session)

	const getInitialFilter = (key) => {
		const values = searchParams.getAll(key)
		return values.length > 0 ? values : []
	}

	// Paginação
	const [page, setPage] = useState(() =>
		Number.parseInt(searchParams.get('page') ?? '1', 10)
	)
	const [limit, setLimit] = useState(() =>
		Number.parseInt(searchParams.get('per_page') ?? '10', 10)
	)
	const [search, setSearch] = useState(
		() => searchParams.get('search') || undefined
	)

	const { filters, setFilter, clearFilters } = useFilters({
		categories: getInitialFilter('categories'),
		status: getInitialFilter('status'),
		priorities: getInitialFilter('priorities'),
		template: getInitialFilter('template'),
		requester: getInitialFilter('requester'),
		responsible: getInitialFilter('responsible'),
		solicitantArea: getInitialFilter('solicitantArea'),
	})

	// Ações
	const debouncedSearch = useDebounce(search)
	const [selectedItem, setSelectedItem] = useState(undefined)
	const [openModal, setOpenModal] = useState(undefined)

	useEffect(() => {
		const params = new URLSearchParams()

		params.set('page', String(page))
		params.set('per_page', String(limit))
		if (debouncedSearch) params.set('search', debouncedSearch)

		Object.keys(filters).forEach((key) => {
			const value = filters[key]
			if (Array.isArray(value) && value.length > 0) {
				value.forEach((item) => params.append(key, item))
			} else if (value && !Array.isArray(value)) {
				params.set(key, value)
			}
		})

		const newSearch = params.toString()
		const currentSearch = location.search.replace(/^\?/, '')

		if (newSearch !== currentSearch) {
			history.replace({
				pathname: location.pathname,
				search: newSearch,
			})
		}
	}, [filters, page, limit, debouncedSearch])

	const updateStatusActions = [
		{
			key: 'action-2',
			label: 'Cancelar',
			onClick: (row) => handleOpenModal('cancel', row),
			icon: <CloseCircleOutlined />,
		},
		{
			key: 'action-4',
			label: 'Concluir',
			onClick: (row) => handleOpenModal('finish', row),
			icon: <CheckOutlined />,
		},
	]

	const adminDemandActions =
		user.perfil === 'admin'
			? [
					{
						key: 'action-3',
						label: 'Atribuir responsável',
						onClick: (row) => handleOpenModal('set-responsible', row),
						icon: <UserSwitchOutlined />,
					},
					...updateStatusActions,
				]
			: []

	const legalDemandActions =
		user.perfil === 'legal'
			? [
					{
						key: 'action-3',
						label: 'Atribuir a mim',
						onClick: (row) => handleOpenModal('set-responsible', row),
						icon: <UserSwitchOutlined />,
					},
					...updateStatusActions,
				]
			: []

	const superAdminActions = user.is_admin
		? [
				{
					key: 'delete',
					label: 'Excluir',
					onClick: (row) => handleOpenModal('delete-demand', row),
					icon: <DeleteOutlined />,
				},
			]
		: []

	const actions = [
		{
			key: 'action-1',
			label: 'Ver detalhes',
			onClick: (row) => history.push(`/demands/${row.id}`),
			icon: <EyeOutlined />,
		},
		...adminDemandActions,
		...legalDemandActions,
		...superAdminActions,
	]

	const columns = useGetColumns({ actions })
	const canViewSensitiveFilters = ['admin', 'legal'].includes(user.perfil)

	const renderFilter = (
		<FilterSidebar
			hasClear={Object.values(filters).some((value) => !!value.length)}
			clearFilters={clearFilters}>
			<FilterButton
				title="Categoria"
				filterKey="category"
				currentSection="demands"
				type="checkbox"
				isPopover={false}
				hasAsyncSearch={true}
				asyncFetch={getCategoriesOptions}
				selected={filters.categories}
				onChange={(val) => setFilter('categories', val)}
			/>
			<FilterButton
				title="Status"
				filterKey="status"
				currentSection="demands"
				type="checkbox"
				options={statusOptions}
				isPopover={false}
				selected={filters.status}
				onChange={(val) => setFilter('status', val)}
			/>
			<FilterButton
				title="Prioridade"
				filterKey="priorities"
				currentSection="demands"
				type="checkbox"
				options={priorityOptions}
				isPopover={false}
				selected={filters.priorities}
				onChange={(val) => setFilter('priorities', val)}
			/>
			<FilterButton
				title="Modelo"
				filterKey="template"
				currentSection="demands"
				type="checkbox"
				isPopover={false}
				hasAsyncSearch={true}
				asyncFetch={getTemplatesOptions}
				selected={filters.template}
				onChange={(val) => setFilter('template', val)}
			/>
			{canViewSensitiveFilters && (
				<>
					<FilterButton
						title="Solicitante"
						filterKey="requester"
						currentSection="demands"
						type="checkbox"
						isPopover={false}
						hasAsyncSearch={true}
						asyncFetch={getUsersOptions}
						selected={filters.requester}
						onChange={(val) => setFilter('requester', val)}
					/>
					<FilterButton
						title="Responsável"
						filterKey="responsible"
						currentSection="demands"
						type="checkbox"
						isPopover={false}
						hasAsyncSearch={true}
						asyncFetch={getUsersOptions}
						selected={filters.responsible}
						onChange={(val) => setFilter('responsible', val)}
					/>
					<FilterButton
						title="Área Solicitante"
						filterKey="solicitantArea"
						currentSection="demands"
						type="checkbox"
						isPopover={false}
						hasAsyncSearch={true}
						asyncFetch={getSolicitantAreasOptions}
						selected={filters.solicitantArea}
						onChange={(val) => setFilter('solicitantArea', val)}
					/>
				</>
			)}
		</FilterSidebar>
	)

	const { data, isLoading, isFetching, isError } = useQuery({
		queryKey: ['demands', page, limit, debouncedSearch, filters],
		queryFn: fetchDemands,
		staleTime: 1000 * 30,
		keepPreviousData: true,
		refetchOnWindowFocus: true,
		retry: false,
	})

	async function fetchDemands() {
		try {
			const statusProcessado = processStatusFilters(filters.status)
			const payloadFilters = {
				categories: filters.categories,
				status: statusProcessado,
				priorities: filters.priorities,
				models: filters.template,
				requesters: filters.requester,
				responsibles: filters.responsible,
				solicitantAreas: filters.solicitantArea,
			}
			const paramsFinais = {
				page,
				limit,
				search: debouncedSearch,
				...payloadFilters,
			}
			const data = await getDemands(paramsFinais)
			return {
				demands: data.list,
				pages: { page: data.page, total: data.count, per_page: limit },
			}
		} catch (error) {
			console.error('Erro no fetch:', error)
			errorMessage({ content: 'Erro ao listar demandas' })
		}
	}

	// --- Mutations ---
	const { mutate: changeDemandStatus, isPending: isLoadingChangeStatus } =
		useMutation({
			mutationFn: async ({ id, status }) => {
				const data = await updateStatus({ id, status })
				return data
			},
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ['demands'] })
				setOpenModal(undefined)
				successMessage({
					content: 'Status da demanda alterado com sucesso!',
				})
			},
			onError: () => {
				errorMessage({
					content: 'Erro ao alterar status da demanda.',
				})
			},
		})

	const { mutate: handleSetResponsible, isPending: isLoadingSetResponsible } =
		useMutation({
			mutationFn: async (values) => {
				const taskId = selectedItem.id
				const data = await setResponsible({
					taskId,
					userId: values.responsible,
				})
				return data
			},
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ['demands'] })
				setOpenModal(undefined)
				successMessage({ content: 'Responsável atribuido com sucesso!' })
			},
			onError: (error) => {
				console.log(error)
				errorMessage({
					content: 'Erro ao atribuir responsável.',
				})
			},
		})

	const { mutate: handleDeleteDemand, isPending: isLoadingDeleteDemand } =
		useMutation({
			mutationFn: async () => {
				const taskId = selectedItem.id
				const data = await deleteTask(taskId)
				return data
			},
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ['demands'] })
				setOpenModal(undefined)
				successMessage({ content: 'Demanda excluída com sucesso!' })
			},
			onError: (error) => {
				console.log(error)
				errorMessage({
					content: 'Erro ao excluir demanda.',
				})
			},
		})

	// --- Handlers ---
	function handleFinishDemand() {
		changeDemandStatus({ id: selectedItem.id, status: 'Concluído' })
	}

	function handleCancelDemand() {
		changeDemandStatus({ id: selectedItem.id, status: 'Cancelado' })
	}

	function handleCloseModal() {
		setOpenModal(undefined)
	}

	function handleOpenModal(modal, item) {
		setOpenModal(modal)
		setSelectedItem(item)
	}

	function handleChangePagination({ page, perPage }) {
		setPage(page)
		setLimit(perPage)
	}

	function handleChangeSearch(currentSearch) {
		setPage(1)
		setSearch(currentSearch)
	}

	if (isError)
		return (
			<MainLayout selectedKey="demands.list">
				<p>Erro ao obter as demandas</p>
			</MainLayout>
		)

	return (
		<MainLayout selectedKey="demands.list">
			<Layout style={{ backgroundColor: '#fff' }}>
				<PageHeader>
					<Breadcrumb>
						<Breadcrumb.Item onClick={() => {}}>Demandas</Breadcrumb.Item>
					</Breadcrumb>
				</PageHeader>
				<Layout style={{ backgroundColor: '#fff' }}>
					<ConfirmationModal
						show={openModal === 'delete-demand'}
						title={'Excluir demanda'}
						text={'Deseja realmente excluir esta demanda? '}
						onCancel={handleCloseModal}
						cancelText="Fechar"
						onConfirm={handleDeleteDemand}
						loading={isLoadingDeleteDemand}
					/>
					<ConfirmationModal
						show={openModal === 'cancel'}
						title={'Cancelar demanda'}
						text={'Deseja realmente cancelar esta demanda?'}
						onCancel={handleCloseModal}
						cancelText="Fechar"
						onConfirm={handleCancelDemand}
						loading={isLoadingChangeStatus}
					/>
					<ConfirmationModal
						show={openModal === 'finish'}
						title={'Concluir demanda'}
						text={'Deseja marcar a demanda como concluída?'}
						onCancel={handleCloseModal}
						onConfirm={handleFinishDemand}
						loading={isLoadingChangeStatus}
					/>
					<SetResponsibleModal
						show={openModal === 'set-responsible'}
						onCancel={handleCloseModal}
						onConfirm={handleSetResponsible}
						loading={isLoadingSetResponsible}
						isLegalUser={user.perfil === 'legal'}
						teamId={selectedItem?.team_id}
					/>
					<DataTable
						rowKey="id"
						columns={columns}
						dataSource={data?.demands ?? []}
						pages={data?.pages ?? { page: 1, per_page: 10, total: 0 }}
						onChangePageNumber={({ page, perPage }) =>
							handleChangePagination({ page, perPage })
						}
						onSearch={({ search }) => handleChangeSearch(search)}
						searchValue={search}
						extraComponent={renderFilter}
						filterOptions={null}
						onFilter={() => {}}
						onClickButton={() => history.push('/demands/new')}
						textButton="Nova Demanda"
						placeholderSearch="Buscar Demanda"
						placeholderNoData={
							!isLoading || isError ? 'Nenhuma demanda encontrada' : ''
						}
						loading={isLoading || isFetching}
						buttons={[]}
						sortTable={() => {}}
					/>
				</Layout>
			</Layout>
		</MainLayout>
	)
}

export default Demands
